import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useHasPermission } from 'hooks';
import { t } from 'i18next';
import { useState } from 'react';
import {
  PiCalendar,
  PiClock,
  PiIdentificationCard,
} from 'react-icons/pi';
import { useNavigate } from 'react-router';
import { useGetEvents } from 'services/events';
import {
  IEventListParams,
  IEventListItem,
} from 'services/events/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';
import { useSearchById, useSetSearchById } from 'store/state';

enum EVENT_COLUMNS {
  name,
  startDate,
  endDate,
  status,
}

const initialValues: IEventListParams = {
  Name: '',
  StartSettlementPeriodDate: '',
  EndSettlementPeriodDate: '',
};

const Events = () => {
  usePermissionGuard('EventPermissionsLIST**');
  const hasPermission = useHasPermission();

  const columnsVisibility = useTableColumnsVisibility('Events', EVENT_COLUMNS);
  const navigate = useNavigate();
  const i18nextPrefix = 'events';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || initialValues;

  const { data, isLoading, isFetchingNextPage, fetchNextPage } =
    useGetEvents(filters);
  const tableData = getNodes<IEventListItem>(data?.pages.flat());

  const hasDetailPermission = hasPermission('EventPermissionsLIST**', 16);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/labels`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t('event.title.list')}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={EVENT_COLUMNS}
                  i18nextPrefix='event'
                  isClear
                  visibility={columnsVisibility}
                />
              </div>
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate('create/labels', { replace: false })}>
                <Button.Text
                text={t('button.create-what', { what: t('event.what') })}
              />
                <Button.Icon icon={<PiCalendar />} />
              </Button>
            </>
          }>
          <QueryFilter<IEventListParams>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IEventListParams>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Text
                    name='Name'
                    label={t('input.event.name')}
                  />
                  <FormInput.Text
                    name='StartSettlementPeriodDate'
                    label={t('input.event.startSettlementPeriodDate')}
                    type='date'
                  />
                  <FormInput.Text
                    name='EndSettlementPeriodDate'
                    label={t('input.event.endSettlementPeriodDate')}
                    type='date'
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        columns={[
          {
            label: t('event.table.name'),
            renderCell: ({ name }) => (
              <TableItem>
                <TableItem.Text text={name} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(EVENT_COLUMNS.name),
          },
          {
            label: t('event.table.startDate'),
            renderCell: ({ startDate }) => (
              <TableItem>
                <TableItem.Text
                  text={startDate ? new Date(startDate).toLocaleDateString() : undefined}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(EVENT_COLUMNS.startDate),
          },
          {
            label: t('event.table.endDate'),
            renderCell: ({ endDate }) => (
              <TableItem>
                <TableItem.Text
                  text={endDate ? new Date(endDate).toLocaleDateString() : undefined}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(EVENT_COLUMNS.endDate),
          },
          {
            label: t('event.table.status'),
            renderCell: ({ status }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={status} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(EVENT_COLUMNS.status),
          },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default Events;
